import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// --- Icons (Inline SVGs) ---
const Icons = {
  Zap: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
  ),
  Layout: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><line x1="3" x2="21" y1="9" y2="9"></line><line x1="9" x2="9" y1="21" y2="9"></line></svg>
  ),
  Users: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
  ),
  CheckCircle: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
  ),
  ArrowRight: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
  ),
  Menu: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" x2="20" y1="12" y2="12"></line><line x1="4" x2="20" y1="6" y2="6"></line><line x1="4" x2="20" y1="18" y2="18"></line></svg>
  ),
  X: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
  ),
  BarChart: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" x2="12" y1="20" y2="10"></line><line x1="18" x2="18" y1="20" y2="4"></line><line x1="6" x2="6" y1="20" y2="16"></line></svg>
  ),
  Shield: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
  ),
  Cpu: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect><rect x="9" y="9" width="6" height="6"></rect><line x1="9" x2="9" y1="1" y2="4"></line><line x1="15" x2="15" y1="1" y2="4"></line><line x1="9" x2="9" y1="20" y2="23"></line><line x1="15" x2="15" y1="20" y2="23"></line><line x1="20" x2="23" y1="9" y2="9"></line><line x1="20" x2="23" y1="14" y2="14"></line><line x1="1" x2="4" y1="9" y2="9"></line><line x1="1" x2="4" y1="14" y2="14"></line></svg>
  ),
  Globe: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" x2="22" y1="12" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
  )
};

const SoftyeKanbanLanding = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.2], [0, -50]);
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] text-white font-sans overflow-x-hidden selection:bg-indigo-500/30">
      {/* --- Background Ambient Effects --- */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-indigo-900/20 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-900/20 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '2s' }} />
        <div className="absolute top-[40%] left-[30%] w-[30%] h-[30%] bg-cyan-900/10 rounded-full blur-[100px]" />
      </div>

      {/* --- Navigation --- */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#030712]/80 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'}`}>
        <div className="container mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <Icons.Zap />
            </div>
            <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">Softye Kanban</span>
          </div>

          <div className="hidden md:flex items-center gap-8">
            {['Features', 'Solutions', 'Pricing', 'About'].map((item) => (
              <button
                key={item}
                onClick={() => scrollTo(item.toLowerCase())}
                className="text-sm font-medium text-gray-400 hover:text-white transition-colors"
              >
                {item}
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <Link to="/login" className="text-sm font-medium text-white hover:text-indigo-400 transition-colors">Log In</Link>
            <Link to="/signup" className="px-5 py-2.5 bg-white text-black rounded-lg font-semibold text-sm hover:bg-gray-200 transition-all transform hover:scale-105 active:scale-95">
              Get Started
            </Link>
          </div>
          <button className="md:hidden p-2 text-gray-400" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <Icons.X /> : <Icons.Menu />}
          </button>
        </div>
      </nav>

      {/* --- Mobile Menu --- */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-[#030712] pt-24 px-6 md:hidden"
          >
            <div className="flex flex-col gap-6 text-center">
              {['Features', 'Solutions', 'Pricing', 'About'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollTo(item.toLowerCase())}
                  className="text-2xl font-medium text-gray-300"
                >
                  {item}
                </button>
              ))}
              <div className="h-px bg-white/10 my-4" />
              <button className="text-xl font-medium text-white">Log In</button>
              <button className="px-6 py-4 bg-indigo-600 text-white rounded-xl font-semibold text-xl">
                Get Started
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* --- Hero Section --- */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 px-6 overflow-hidden">
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="flex flex-col items-center text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8 backdrop-blur-sm"
            >
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-sm font-medium text-gray-300">v2.0 is now live</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-b from-white via-white to-gray-500"
            >
              Manage Projects <br />
              <span className="text-indigo-500">at Light Speed</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg md:text-xl text-gray-400 max-w-2xl mb-10 leading-relaxed"
            >
              The all-in-one workspace for agile teams. Streamline your workflow,
              automate mundane tasks, and ship products faster than ever before.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto"
            >
              <button className="px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-semibold text-lg transition-all shadow-lg shadow-indigo-500/25 flex items-center justify-center gap-2 group">
                Start Free Trial
                <motion.span animate={{ x: [0, 4, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
                  <Icons.ArrowRight />
                </motion.span>
              </button>
              <button className="px-8 py-4 bg-white/5 hover:bg-white/10 text-white rounded-xl font-semibold text-lg border border-white/10 transition-all backdrop-blur-sm">
                View Demo
              </button>
            </motion.div>
          </div>

          {/* Hero Dashboard Preview */}
          <motion.div
            style={{ y: heroY, opacity }}
            className="relative mx-auto max-w-6xl"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl blur opacity-30" />
            <div className="relative bg-[#0B0F19] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-[#0B0F19]">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50" />
                  <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50" />
                </div>
                <div className="ml-4 px-3 py-1 bg-white/5 rounded-md text-xs text-gray-500 font-mono">softye.app/board/sprint-24</div>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6 h-[400px] md:h-[600px] overflow-hidden">
                {/* Mock Columns */}
                {[
                  { title: 'To Do', color: 'bg-gray-500', items: 3 },
                  { title: 'In Progress', color: 'bg-indigo-500', items: 2 },
                  { title: 'Done', color: 'bg-green-500', items: 4 }
                ].map((col, i) => (
                  <div key={i} className="bg-white/5 rounded-xl p-4 flex flex-col gap-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${col.color}`} />
                        <span className="font-medium text-sm text-gray-300">{col.title}</span>
                      </div>
                      <span className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded">{col.items}</span>
                    </div>
                    {[...Array(col.items)].map((_, j) => (
                      <motion.div
                        key={j}
                        whileHover={{ y: -2, backgroundColor: 'rgba(255,255,255,0.08)' }}
                        className="bg-white/5 p-4 rounded-lg border border-white/5 cursor-pointer group"
                      >
                        <div className="h-2 w-16 bg-white/10 rounded mb-3" />
                        <div className="h-4 w-3/4 bg-white/20 rounded mb-2" />
                        <div className="h-3 w-1/2 bg-white/10 rounded mb-4" />
                        <div className="flex items-center justify-between">
                          <div className="flex -space-x-2">
                            <div className="w-6 h-6 rounded-full bg-indigo-500 border border-[#0B0F19]" />
                            <div className="w-6 h-6 rounded-full bg-purple-500 border border-[#0B0F19]" />
                          </div>
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-500">
                            <Icons.ArrowRight />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- Social Proof --- */}
      <section className="py-10 border-y border-white/5 bg-white/[0.02]">
        <div className="container mx-auto px-6">
          <p className="text-center text-sm text-gray-500 mb-8 font-medium">TRUSTED BY INNOVATIVE TEAMS WORLDWIDE</p>
          <div className="flex flex-wrap justify-center gap-12 md:gap-20 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
            {['Acme Corp', 'GlobalTech', 'Nebula', 'Vertex', 'Spherix'].map((brand) => (
              <span key={brand} className="text-xl font-bold text-white">{brand}</span>
            ))}
          </div>
        </div>
      </section>

      {/* --- Features Bento Grid --- */}
      <section id="features" className="py-32 px-6 relative">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">Everything you need to <br /><span className="text-indigo-500">ship faster</span></h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Powerful features designed for modern software teams. From planning to deployment, we've got you covered.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Large Card */}
            <motion.div
              whileHover={{ y: -5 }}
              className="md:col-span-2 bg-gradient-to-br from-indigo-900/20 to-purple-900/20 border border-white/10 rounded-3xl p-8 md:p-12 relative overflow-hidden group"
            >
              <div className="relative z-10">
                <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center mb-6 text-indigo-400">
                  <Icons.BarChart />
                </div>
                <h3 className="text-2xl font-bold mb-4">Real-time Analytics</h3>
                <p className="text-gray-400 max-w-md">Gain insights into your team's performance with detailed charts and sprint velocity tracking.</p>
              </div>
              <div className="absolute right-0 bottom-0 w-1/2 h-full bg-gradient-to-l from-indigo-500/10 to-transparent" />
              <div className="absolute right-[-20px] bottom-[-20px] opacity-50 group-hover:opacity-100 transition-all duration-500 transform group-hover:scale-105">
                {/* Abstract Chart Graphic */}
                <div className="w-64 h-48 bg-white/5 rounded-tl-2xl border-t border-l border-white/10 p-4">
                  <div className="flex items-end gap-2 h-full">
                    <div className="w-8 h-[40%] bg-indigo-500/50 rounded-t" />
                    <div className="w-8 h-[70%] bg-indigo-500/70 rounded-t" />
                    <div className="w-8 h-[50%] bg-indigo-500/40 rounded-t" />
                    <div className="w-8 h-[90%] bg-indigo-500 rounded-t" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Tall Card */}
            <motion.div
              whileHover={{ y: -5 }}
              className="bg-[#0B0F19] border border-white/10 rounded-3xl p-8 relative overflow-hidden"
            >
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-6 text-green-400">
                <Icons.Zap />
              </div>
              <h3 className="text-xl font-bold mb-4">Automations</h3>
              <p className="text-gray-400 text-sm mb-6">Save time by automating repetitive tasks with our visual builder.</p>
              <div className="space-y-3">
                {['Auto-assign tasks', 'Move to Done', 'Send Slack alert'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-white/5">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-sm text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Small Card */}
            <motion.div
              whileHover={{ y: -5 }}
              className="bg-[#0B0F19] border border-white/10 rounded-3xl p-8"
            >
              <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center mb-6 text-pink-400">
                <Icons.Users />
              </div>
              <h3 className="text-xl font-bold mb-4">Team Sync</h3>
              <p className="text-gray-400 text-sm">Collaborate in real-time with comments, mentions, and live updates.</p>
            </motion.div>

            {/* Wide Card */}
            <motion.div
              whileHover={{ y: -5 }}
              className="md:col-span-2 bg-[#0B0F19] border border-white/10 rounded-3xl p-8 flex flex-col md:flex-row items-center gap-8"
            >
              <div className="flex-1">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-6 text-blue-400">
                  <Icons.Globe />
                </div>
                <h3 className="text-xl font-bold mb-4">Global Infrastructure</h3>
                <p className="text-gray-400 text-sm">
                  Hosted on edge networks worldwide ensuring low latency and 99.99% uptime for your mission-critical projects.
                </p>
              </div>
              <div className="flex-1 w-full">
                <div className="grid grid-cols-2 gap-3">
                  {['US East', 'EU West', 'Asia Pacific', 'South America'].map((region) => (
                    <div key={region} className="flex items-center gap-2 p-2 bg-white/5 rounded border border-white/5">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      <span className="text-xs text-gray-400">{region}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- Pricing --- */}
      <section id="pricing" className="py-32 px-6 bg-gradient-to-b from-transparent to-indigo-900/10">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">Simple Pricing</h2>
            <p className="text-gray-400">Start for free, scale as you grow.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { name: 'Starter', price: '$0', desc: 'For individuals', features: ['Up to 3 projects', 'Basic analytics', 'Community support'] },
              { name: 'Pro', price: '$29', desc: 'For growing teams', features: ['Unlimited projects', 'Advanced automation', 'Priority support', 'Custom fields'], popular: true },
              { name: 'Enterprise', price: 'Custom', desc: 'For large orgs', features: ['SSO & Security', 'Dedicated manager', 'SLA', 'On-premise option'] }
            ].map((plan, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -10 }}
                className={`relative p-8 rounded-3xl border ${plan.popular ? 'bg-white/5 border-indigo-500 shadow-2xl shadow-indigo-500/20' : 'bg-[#0B0F19] border-white/10'}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-indigo-500 text-white text-xs font-bold rounded-full uppercase tracking-wider">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  {plan.price !== 'Custom' && <span className="text-gray-500">/mo</span>}
                </div>
                <p className="text-gray-400 text-sm mb-8">{plan.desc}</p>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm text-gray-300">
                      <div className="text-green-400"><Icons.CheckCircle /></div>
                      {f}
                    </li>
                  ))}
                </ul>
                <button className={`w-full py-3 rounded-xl font-semibold transition-all ${plan.popular ? 'bg-indigo-600 hover:bg-indigo-500 text-white' : 'bg-white/5 hover:bg-white/10 text-white'}`}>
                  Choose {plan.name}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- CTA --- */}
      <section className="py-32 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="relative rounded-[3rem] bg-gradient-to-r from-indigo-600 to-blue-600 p-12 md:p-24 text-center overflow-hidden">
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay"></div>
            <div className="relative z-10">
              <h2 className="text-4xl md:text-6xl font-bold mb-8">Ready to ship?</h2>
              <p className="text-indigo-100 text-xl mb-10 max-w-2xl mx-auto">
                Join 10,000+ developers and managers who are already using Softye to manage their projects.
              </p>
              <button className="px-10 py-5 bg-white text-indigo-600 rounded-2xl font-bold text-xl hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl">
                Get Started for Free
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* --- Footer --- */}
      <footer className="border-t border-white/10 bg-[#030712] pt-20 pb-10 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-16">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                  <Icons.Zap />
                </div>
                <span className="text-xl font-bold">Softye</span>
              </div>
              <p className="text-gray-500 text-sm">
                Making project management simple, fast, and enjoyable for everyone.
              </p>
            </div>
            {[
              { title: 'Product', links: ['Features', 'Integrations', 'Pricing', 'Changelog'] },
              { title: 'Company', links: ['About', 'Careers', 'Blog', 'Contact'] },
              { title: 'Legal', links: ['Privacy', 'Terms', 'Security'] }
            ].map((col) => (
              <div key={col.title}>
                <h4 className="font-bold mb-6">{col.title}</h4>
                <ul className="space-y-4">
                  {col.links.map((link) => (
                    <li key={link}>
                      <a href="#" className="text-gray-500 hover:text-white transition-colors text-sm">{link}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-white/10 text-gray-600 text-sm">
            <p>© 2024 Softye Inc. All rights reserved.</p>
            <div className="flex gap-6 mt-4 md:mt-0">
              <a href="#" className="hover:text-white transition-colors">Twitter</a>
              <a href="#" className="hover:text-white transition-colors">GitHub</a>
              <a href="#" className="hover:text-white transition-colors">Discord</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SoftyeKanbanLanding;